<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && tire"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3" :style="{ backgroundColor: 'var(--brand-primary)' + '20' }">
                  <svg class="w-6 h-6" :style="{ color: 'var(--brand-primary)' }" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
                    <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                  </svg>
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                  Tire Registration Details
                </h3>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div class="px-6 py-6">
              <div class="mb-6">
                <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">
                  Approval Status
                </label>
                <span
                  :class="getStatusClass(tire.approval_status)"
                  class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                >
                  <span
                    class="w-2 h-2 rounded-full mr-2"
                    :class="getStatusDotClass(tire.approval_status)"
                  ></span>
                  {{ tire.approval_status }}
                </span>
              </div>

              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  Customer Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Customer Name</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.user?.first_name }} {{ tire.user?.last_name }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Email</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium break-all">{{ tire.user?.email }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Mobile Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.user?.user_detail?.mobile || tire.user?.userDetail?.mobile || 'N/A' }}</p>
                  </div>
                </div>
              </div>

              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/>
                  </svg>
                  Vehicle Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Vehicle</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.vehicle?.vehicle_make }} {{ tire.vehicle?.vehicle_model }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Year</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.vehicle?.vehicle_year }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Plate Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.vehicle?.plate_number }}</p>
                  </div>
                </div>
              </div>

              <!-- Shop Details Section -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 2L3 7v11h14V7l-7-5zM5 9h10v7H5V9z" clip-rule="evenodd" />
                  </svg>
                  Shop Details
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Shop</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.shop }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Store Code</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.code }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Invoice Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.invoice_number }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Submitted Date</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ formatDate(tire.created_at) }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Date of Purchase</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.purchase_date ? formatDateOnly(tire.purchase_date) : 'N/A' }}</p>
                  </div>
                </div>

                <div v-if="tire.receipt_photos && tire.receipt_photos.length > 0" class="mt-4">
                  <label class="text-xs font-medium uppercase tracking-wide mb-2 block" style="color: var(--brand-primary);">Receipt Photos ({{ tire.receipt_photos.length }})</label>
                  <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    <div
                      v-for="(photo, index) in tire.receipt_photos"
                      :key="photo.receipt_photo_id"
                      class="relative group cursor-pointer"
                      @click="openPhotoGallery(index)"
                    >
                      <img
                        :src="getPhotoUrl(photo.photo_path)"
                        :alt="`Receipt photo ${index + 1}`"
                        class="w-full h-32 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                      >
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                        <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tire Information Section -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none"/>
                    <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none"/>
                    <circle cx="12" cy="12" r="2" fill="currentColor"/>
                  </svg>
                  Tire Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Brand</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.brand?.brand_name }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Pattern</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.pattern }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Size</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.tire_size }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Position</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.tire_position }}</p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">DOT</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ tire.dot }}</p>
                  </div>
                </div>

                <div v-if="tire.tire_photos && tire.tire_photos.length > 0" class="mt-4">
                  <label class="text-xs font-medium uppercase tracking-wide mb-2 block" style="color: var(--brand-primary);">Tire Photo</label>
                  <div class="flex flex-wrap gap-4">
                    <div
                      v-for="(photo, index) in tire.tire_photos"
                      :key="photo.tire_photo_id"
                      class="relative group cursor-pointer"
                      @click="openTirePhotoGallery(index)"
                    >
                      <img
                        :src="getPhotoUrl(photo.photo_path)"
                        :alt="`Tire photo ${index + 1}`"
                        class="h-32 w-auto object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                      >
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                        <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="tire.rejection_reason" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                  </svg>
                  Rejection Reason
                </h3>

                <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p class="text-gray-900 dark:text-white">{{ tire.rejection_reason }}</p>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end">
              <button
                @click="$emit('close')"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div
        v-if="photoGalleryOpen && tire"
        class="fixed inset-0 z-[60] bg-black bg-opacity-95 flex items-center justify-center p-4"
        @click="closePhotoGallery"
      >
        <button
          @click="closePhotoGallery"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <button
          v-if="currentPhotoIndex > 0"
          @click.stop="previousPhoto"
          class="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div class="max-w-7xl max-h-full flex flex-col items-center" @click.stop>
          <div class="bg-black bg-opacity-75 text-white px-6 py-3 rounded-t-lg mb-2 w-full">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Receipt Photo</h3>
                <p class="text-sm text-gray-300">{{ tire.pattern }} - {{ tire.tire_size }}</p>
              </div>
              <div class="text-sm text-gray-300">
                Photo {{ currentPhotoIndex + 1 }} of {{ tire.receipt_photos?.length || 0 }}
              </div>
            </div>
          </div>

          <img
            v-if="tire.receipt_photos && tire.receipt_photos[currentPhotoIndex]"
            :src="getPhotoUrl(tire.receipt_photos[currentPhotoIndex].photo_path)"
            :alt="`Receipt Photo ${currentPhotoIndex + 1}`"
            class="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
          />
        </div>

        <button
          v-if="tire.receipt_photos && currentPhotoIndex < tire.receipt_photos.length - 1"
          @click.stop="nextPhoto"
          class="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div
          v-if="tire.receipt_photos && tire.receipt_photos.length > 1"
          class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-75 p-3 rounded-lg max-w-full overflow-x-auto"
          @click.stop
        >
          <button
            v-for="(photo, index) in tire.receipt_photos"
            :key="photo.receipt_photo_id"
            @click="currentPhotoIndex = index"
            class="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all duration-200"
            :class="currentPhotoIndex === index ? 'border-white scale-110' : 'border-gray-600 opacity-60 hover:opacity-100'"
          >
            <img
              :src="getPhotoUrl(photo.photo_path)"
              :alt="`Thumbnail ${index + 1}`"
              class="w-full h-full object-cover"
            />
          </button>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div
        v-if="tirePhotoGalleryOpen && tire && tire.tire_photos && tire.tire_photos.length > 0"
        class="fixed inset-0 z-[60] bg-black bg-opacity-95 flex items-center justify-center p-4"
        @click="closeTirePhotoGallery"
      >
        <button
          @click="closeTirePhotoGallery"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="max-w-7xl max-h-full flex flex-col items-center" @click.stop>
          <div class="bg-black bg-opacity-75 text-white px-6 py-3 rounded-t-lg mb-2 w-full">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Tire Photo</h3>
                <p class="text-sm text-gray-300">{{ tire.pattern }} - {{ tire.tire_size }}</p>
              </div>
            </div>
          </div>

          <img
            :src="getPhotoUrl(tire.tire_photos[currentTirePhotoIndex].photo_path)"
            :alt="`Tire Photo ${currentTirePhotoIndex + 1}`"
            class="max-w-full max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { Tire } from '@/types/tire';

defineProps<{
  isOpen: boolean;
  tire: Tire | null;
}>();

defineEmits<{
  close: [];
}>();

const photoGalleryOpen = ref(false);
const currentPhotoIndex = ref(0);
const tirePhotoGalleryOpen = ref(false);
const currentTirePhotoIndex = ref(0);

function openPhotoGallery(index: number) {
  currentPhotoIndex.value = index;
  photoGalleryOpen.value = true;
}

function closePhotoGallery() {
  photoGalleryOpen.value = false;
  currentPhotoIndex.value = 0;
}

function previousPhoto() {
  if (currentPhotoIndex.value > 0) {
    currentPhotoIndex.value--;
  }
}

function nextPhoto() {
  currentPhotoIndex.value++;
}

function openTirePhotoGallery(index: number) {
  currentTirePhotoIndex.value = index;
  tirePhotoGalleryOpen.value = true;
}

function closeTirePhotoGallery() {
  tirePhotoGalleryOpen.value = false;
  currentTirePhotoIndex.value = 0;
}

function getPhotoUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-black/30 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500 dark:bg-yellow-400';
    case 'approved':
      return 'bg-green-500 dark:bg-green-400';
    case 'rejected':
      return 'bg-red-500 dark:bg-red-400';
    default:
      return 'bg-gray-500 dark:bg-gray-400';
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDateOnly(dateString: string): string {
  // Parse YYYY-MM-DD without time-zone shifting by splitting manually
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
